import { useState } from "react";
import { Link, useParams } from "@tanstack/react-router";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import type { Rolle } from "@cubic/shared";
import { Archive, RotateCcw } from "lucide-react";
import { Button } from "@/components/Button";
import { Fehlerzeile } from "@/components/Fehlerzeile";
import { Input } from "@/components/Input";
import { Label } from "@/components/Label";
import { Leerzustand } from "@/components/Leerzustand";
import { Planabhaengigkeiten } from "@/components/Planabhaengigkeiten";
import { Textarea } from "@/components/Textarea";
import { Fortschrittslinie } from "@/components/entwurf/Fortschrittslinie";
import { Kennzahl } from "@/components/entwurf/Kennzahl";
import { Mikrolabel } from "@/components/entwurf/Mikrolabel";
import { Rubrik } from "@/components/entwurf/Rubrik";
import { Zahlenband } from "@/components/entwurf/Zahlenband";
import { Kennung, Zeilentitel } from "@/components/entwurf/Zeile";
import { verweisKlassen } from "@/components/entwurf/Verweis";
import { api } from "@/lib/api";
import {
  fehlerMeldung,
  useArbeitspaket,
  useArbeitspaketFortschritt,
  useAufgabenFuerProjekte,
  useIch,
  useMeilensteine,
  useProjekt,
} from "@/lib/haken";
import { leererFilter } from "@/lib/filter";
import { darfAendern } from "@/lib/rollen";
import { cx, focusRing } from "@/lib/utils";

export function ArbeitspaketDetail() {
  const { projektId, arbeitspaketId } = useParams({
    from: "/_app/projekte/$projektId/arbeitspakete/$arbeitspaketId",
  });
  const client = useQueryClient();
  const ich = useIch();
  const projekt = useProjekt(projektId);
  const paket = useArbeitspaket(arbeitspaketId);
  const fortschritt = useArbeitspaketFortschritt(arbeitspaketId);
  const meilensteine = useMeilensteine(projektId);
  const aufgaben = useAufgabenFuerProjekte([projektId], leererFilter());
  const [bearbeitet, setzeBearbeitet] = useState(false);
  const [name, setzeName] = useState("");
  const [beschreibung, setzeBeschreibung] = useState("");
  const [frist, setzeFrist] = useState("");
  const [meilensteinId, setzeMeilensteinId] = useState("");
  const [neueAufgabe, setzeNeueAufgabe] = useState("");
  const [meldung, setzeMeldung] = useState<ReturnType<typeof fehlerMeldung> | null>(null);

  const rolle = ich.data?.projekte.find(
    (eintrag) => eintrag.projektId === projektId,
  )?.rolle as Rolle | undefined;
  const kann = darfAendern(rolle, { rechte: ich.data?.rechte });

  async function aktualisieren() {
    setzeMeldung(null);
    await Promise.all([
      paket.refetch(),
      fortschritt.refetch(),
      aufgaben.refetch(),
      client.invalidateQueries({ queryKey: ["cubic", "arbeitspakete"] }),
      client.invalidateQueries({ queryKey: ["cubic", "meilenstein"] }),
    ]);
  }

  const speichern = useMutation({
    mutationFn: () =>
      api.arbeitspakete.aendern.mutate({
        id: arbeitspaketId,
        erwarteteVersion: paket.data!.version,
        name: name.trim(),
        beschreibung,
        meilensteinId,
        faelligAm: frist ? new Date(`${frist}T12:00:00.000Z`) : null,
      }),
    onSuccess: async () => {
      setzeBearbeitet(false);
      await aktualisieren();
    },
    onError: (fehler) => setzeMeldung(fehlerMeldung(fehler)),
  });
  const archivieren = useMutation({
    mutationFn: () =>
      paket.data?.archiviertAm
        ? api.arbeitspakete.wiederherstellen.mutate({ id: arbeitspaketId })
        : api.arbeitspakete.archivieren.mutate({
            id: arbeitspaketId,
            erwarteteVersion: paket.data!.version,
          }),
    onSuccess: aktualisieren,
    onError: (fehler) => setzeMeldung(fehlerMeldung(fehler)),
  });
  const aufgabeAnlegen = useMutation({
    mutationFn: () =>
      api.aufgaben.anlegen.mutate({
        projektId,
        titel: neueAufgabe.trim(),
        arbeitspaketId,
      }),
    onSuccess: async () => {
      setzeNeueAufgabe("");
      await aktualisieren();
    },
    onError: (fehler) => setzeMeldung(fehlerMeldung(fehler)),
  });

  if (projekt.isLoading || paket.isLoading) {
    return <Mikrolabel grad={12}>Lade Arbeitspaket …</Mikrolabel>;
  }
  const p = projekt.data;
  const ap = paket.data;
  if (!p || !ap || ap.projektId !== projektId) {
    return (
      <Leerzustand
        titel="Dieses Arbeitspaket gibt es nicht."
        hinweis="Es ist nicht sichtbar oder die Adresse stimmt nicht."
      />
    );
  }
  const meilenstein = meilensteine.data?.find(
    (eintrag) => eintrag.id === ap.meilensteinId,
  );
  const zugehoerig = (aufgaben.data ?? []).filter(
    (aufgabe) => aufgabe.arbeitspaketId === ap.id && !aufgabe.archiviertAm,
  );
  const wurzeln = zugehoerig.filter((aufgabe) => !aufgabe.elternId);
  const anteil = Math.round((fortschritt.data?.anteil ?? 0) * 100);

  function bearbeitungOeffnen() {
    if (!ap) return;
    setzeName(ap.name);
    setzeBeschreibung(ap.beschreibung);
    setzeFrist(
      ap.faelligAm ? new Date(ap.faelligAm).toISOString().slice(0, 10) : "",
    );
    setzeMeilensteinId(ap.meilensteinId);
    setzeBearbeitet(true);
  }

  return (
    <div className="mx-auto w-full max-w-[980px] px-4 pb-24 pt-8 sm:px-6 lg:px-9 lg:pt-14">
      {meldung ? <Fehlerzeile meldung={meldung} /> : null}
      <Link
        to="/projekte/$projektId/meilensteine/$meilensteinId"
        params={{ projektId, meilensteinId: ap.meilensteinId }}
        className={verweisKlassen({ grad: 12 })}
      >
        ← {meilenstein?.name ?? "Meilenstein"}
      </Link>
      <div className="mt-6 flex flex-wrap items-start justify-between gap-5">
        <div>
          <Mikrolabel grad={12.5}>{`${p.kuerzel}-AP${ap.nummer}`}</Mikrolabel>
          <h1 className="mt-2 text-[40px] font-extralight leading-[1.1] tracking-[-0.034em] text-tinte">
            {ap.name}
          </h1>
          {ap.beschreibung ? (
            <p className="mt-4 max-w-[62ch] text-[15px] font-light leading-7 text-tinte2">
              {ap.beschreibung}
            </p>
          ) : null}
        </div>
        {kann ? (
          <div className="flex gap-2">
            <Button type="button" variant="secondary" onClick={bearbeitungOeffnen}>
              Bearbeiten
            </Button>
            <Button
              type="button"
              variant="secondary"
              onClick={() => archivieren.mutate()}
              disabled={archivieren.isPending}
            >
              {ap.archiviertAm ? (
                <RotateCcw className="mr-1.5 size-4" />
              ) : (
                <Archive className="mr-1.5 size-4" />
              )}
              {ap.archiviertAm ? "Wiederherstellen" : "Archivieren"}
            </Button>
          </div>
        ) : null}
      </div>

      <Zahlenband className="mt-8">
        <Kennzahl label="Fortschritt" grad={56} wert={`${anteil}%`} />
        <Kennzahl
          label="erledigt"
          grad={56}
          wert={`${fortschritt.data?.erledigt ?? 0}/${fortschritt.data?.gesamt ?? 0}`}
        />
        <Kennzahl label="Aufgaben" grad={56} wert={String(wurzeln.length)} />
      </Zahlenband>

      {bearbeitet ? (
        <form
          className="mt-10 grid gap-4 border-y border-linie py-5 sm:grid-cols-2"
          onSubmit={(ereignis) => {
            ereignis.preventDefault();
            if (name.trim() && meilensteinId) speichern.mutate();
          }}
        >
          <div>
            <Label htmlFor="arbeitspaket-name">Name</Label>
            <Input id="arbeitspaket-name" className="mt-1" value={name} onChange={(e) => setzeName(e.target.value)} />
          </div>
          <div>
            <Label htmlFor="arbeitspaket-meilenstein">Meilenstein</Label>
            <select
              id="arbeitspaket-meilenstein"
              className="mt-1 h-10 w-full border border-linie bg-papier px-3 text-sm"
              value={meilensteinId}
              onChange={(e) => setzeMeilensteinId(e.target.value)}
            >
              {(meilensteine.data ?? []).map((eintrag) => (
                <option key={eintrag.id} value={eintrag.id}>{eintrag.name}</option>
              ))}
            </select>
          </div>
          <div className="sm:col-span-2">
            <Label htmlFor="arbeitspaket-beschreibung">Beschreibung</Label>
            <Textarea id="arbeitspaket-beschreibung" className="mt-1" value={beschreibung} onChange={(e) => setzeBeschreibung(e.target.value)} />
          </div>
          <div>
            <Label htmlFor="arbeitspaket-frist">Frist</Label>
            <Input id="arbeitspaket-frist" className="mt-1" type="date" value={frist} onChange={(e) => setzeFrist(e.target.value)} />
          </div>
          <div className="flex items-end gap-2">
            <Button type="submit" disabled={!name.trim() || !meilensteinId || speichern.isPending}>Speichern</Button>
            <Button type="button" variant="secondary" onClick={() => setzeBearbeitet(false)}>Abbrechen</Button>
          </div>
        </form>
      ) : null}

      <div className="mt-13">
        <Rubrik anzahl={`${wurzeln.length}`}>Aufgaben</Rubrik>
        {wurzeln.map((aufgabe) => {
          const kinder = zugehoerig.filter((kind) => kind.elternId === aufgabe.id);
          return (
            <div key={aufgabe.id} className="border-b border-linie py-3">
              <Link
                to="/aufgaben/$id"
                params={{ id: aufgabe.id }}
                className={cx("flex items-center gap-3 hover:bg-wisch", focusRing)}
              >
                <Kennung>{`${p.kuerzel}-${aufgabe.nummer}`}</Kennung>
                <Zeilentitel titel={aufgabe.titel}>{aufgabe.titel}</Zeilentitel>
              </Link>
              {kinder.map((kind) => (
                <Link
                  key={kind.id}
                  to="/aufgaben/$id"
                  params={{ id: kind.id }}
                  className="ml-8 mt-2 flex items-center gap-3 border-l border-linie pl-3 text-sm hover:text-akzent"
                >
                  <Kennung>{`${p.kuerzel}-${kind.nummer}`}</Kennung>
                  <span>{kind.titel}</span>
                </Link>
              ))}
            </div>
          );
        })}
        {wurzeln.length === 0 ? (
          <Mikrolabel grad={11} className="block border-b border-linie py-5">
            Noch keine Aufgaben in diesem Arbeitspaket.
          </Mikrolabel>
        ) : null}
        {kann && !ap.archiviertAm ? (
          <form
            className="mt-4 flex gap-2"
            onSubmit={(ereignis) => {
              ereignis.preventDefault();
              if (neueAufgabe.trim()) aufgabeAnlegen.mutate();
            }}
          >
            <Input
              value={neueAufgabe}
              placeholder="Aufgabe hinzufügen …"
              onChange={(e) => setzeNeueAufgabe(e.target.value)}
            />
            <Button type="submit" disabled={!neueAufgabe.trim() || aufgabeAnlegen.isPending}>
              Hinzufügen
            </Button>
          </form>
        ) : null}
      </div>

      <Planabhaengigkeiten
        objekt={{ art: "arbeitspaket", id: arbeitspaketId }}
        kannAendern={kann && !Boolean(ap.archiviertAm)}
      />
      <div className="mt-10">
        <Fortschrittslinie anteil={anteil} label="Arbeitspaket erledigt" />
      </div>
    </div>
  );
}
